import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrgId } from 'shared/models';
import { getUsersRequest } from 'actions/userActions';
import { useAsync } from 'hooks/useAsync/useAsync';
import { getUsers, getUsersError } from 'selectors/usersSelectors';
import { pickBy } from 'lodash';

export function useOrgUsers(orgId?: OrgId | null) {
    const { isMounted } = useAsync();

    const [ loading, setLoading ] = useState(false);
    const allUsers = useSelector(getUsers);
    const error = useSelector(getUsersError);

    const orgUsers = useMemo(() => {
        return pickBy(allUsers, user => user.orgId === orgId);
    }, [ allUsers, orgId ]);

    const dispatch = useDispatch();

    //
    // Load all the organization users via the getUsersSaga
    //
    const loadOrgUsers = useCallback(() => {
        if (!orgId) return;
        setLoading(true);

        void (async () => {
            let page = 1;
            let more = true;
            while (more && isMounted()) {
                more = await new Promise<boolean>(resolve => {
                    const filter = {
                        orgs: { [orgId]: true },
                        maxRows: 100,
                        page,
                    };
                    dispatch(getUsersRequest(filter, {
                        callback: (_, more) => resolve(more),
                    }));
                });
                page++;
            }

            if (isMounted()) {
                setLoading(false);
            }
        })();
    }, [ orgId, isMounted, setLoading, dispatch ]);

    //
    // Begin loading the organization's users when this hook mounts
    //
    useEffect(() => {
        loadOrgUsers();
    }, [ loadOrgUsers ]);

    return {
        orgUsers,
        loadingOrgUsers: loading,
        error,
    };
}
